import React, { useState, useCallback, useEffect } from 'react';
import { ServiceRequestFormData } from '../../types';
import './StepStyles.scss';

interface Step2DetailsProps {
  formData: ServiceRequestFormData;
  updateFormData: (data: Partial<ServiceRequestFormData>) => void;
  // Function passed down from MultiStepForm to trigger validation check
  registerValidation: (validator: () => boolean) => void;
}

// Basic Email Validation Regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Basic US Zip Code Regex (5 digits)
const ZIP_REGEX = /^\d{5}$/;
// Basic State Abbreviation Regex (2 letters) - adapt if needed for international
const STATE_REGEX = /^[A-Za-z]{2}$/;

function Step2Customer({
  formData,
  updateFormData,
  registerValidation,
}: Step2DetailsProps) {
  const [errors, setErrors] = useState<Partial<Record<keyof ServiceRequestFormData, string>>>({});

  // --- Form Input Handling ---
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    updateFormData({ [name]: newValue });

    // Clear error for this field on change without causing a re-render
    if (errors[name as keyof ServiceRequestFormData]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name as keyof ServiceRequestFormData];
        return next;
      });
    }
  };

  // --- Validation ---
  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof ServiceRequestFormData, string>> = {};
    let isValid = true;

    // Helper to check required fields
    const checkRequired = (field: keyof ServiceRequestFormData, message = 'This field is required') => {
      if (!formData[field] || (typeof formData[field] === 'string' && !(formData[field] as string).trim())) {
        newErrors[field] = message;
        isValid = false;
      }
    };

    // Check required fields
    checkRequired('firstName');
    checkRequired('lastName');
    checkRequired('email');
    checkRequired('address');
    checkRequired('city');
    checkRequired('state');
    checkRequired('zipCode');
    checkRequired('preferredContact');

    // Email format
    if (formData.email && !EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

     // At least one phone number if preferred contact is mobile or home
    if (formData.preferredContact === 'mobile' && (!formData.mobilePhone || !formData.mobilePhone.trim())) {
        newErrors.mobilePhone = 'Mobile phone is required if selected as preferred contact';
        isValid = false;
    }
    if (formData.preferredContact === 'home' && (!formData.homePhone || !formData.homePhone.trim())) {
        newErrors.homePhone = 'Home phone is required if selected as preferred contact';
        isValid = false;
    }

    // Zip code format
    if (formData.zipCode && !ZIP_REGEX.test(formData.zipCode)) {
        newErrors.zipCode = 'Please enter a valid 5-digit zip code';
        isValid = false;
    }

     // State format (optional strictness)
     if (formData.state && !STATE_REGEX.test(formData.state)) {
        newErrors.state = 'Please enter a valid 2-letter state abbreviation';
        isValid = false;
     }


    setErrors(newErrors);
    return isValid;
  }, [formData]);

  // Register the validation function with the parent form
  useEffect(() => {
    registerValidation(validateForm);
  }, [registerValidation, validateForm]); // Re-register if validateForm changes

  // --- Render ---
  const getError = (field: keyof ServiceRequestFormData) => errors[field];

  return (
    <div className="form-step">
      <h2>Your Information</h2>
      <p className="step-intro">Please provide your contact details so we can reach you regarding your request.</p>

      {/* Overall Form Error Display */}
      {Object.keys(errors).length > 0 && (
        <div className="error-message summary-error">
          Please review the highlighted fields and correct any errors.
        </div>
      )}

      {/* Form Fields */}
      <div className="form-row">
         <div className="form-group">
            <label htmlFor="firstName" className="required">First Name</label>
            <input 
              type="text" 
              id="firstName" 
              name="firstName" 
              value={formData.firstName || ''} 
              onChange={handleChange} 
              required 
              className={getError('firstName') ? 'is-invalid' : ''}
            />
            {getError('firstName') && <span className="error-text">{getError('firstName')}</span>}
         </div>
         <div className="form-group">
            <label htmlFor="lastName" className="required">Last Name</label>
            <input 
              type="text" 
              id="lastName" 
              name="lastName" 
              value={formData.lastName || ''} 
              onChange={handleChange} 
              required 
              className={getError('lastName') ? 'is-invalid' : ''}
            />
             {getError('lastName') && <span className="error-text">{getError('lastName')}</span>}
         </div>
      </div>

      <div className="form-group">
         <label htmlFor="email" className="required">Email Address</label>
         <input 
           type="email" 
           id="email" 
           name="email" 
           value={formData.email || ''} 
           onChange={handleChange} 
           required 
           placeholder="you@example.com"
           className={getError('email') ? 'is-invalid' : ''}
         />
          {getError('email') && <span className="error-text">{getError('email')}</span>}
      </div>

      {/* Rest of the form fields remain the same */}
      <div className="form-row">
         <div className="form-group">
            <label htmlFor="mobilePhone">Mobile Phone</label>
            <input 
              type="tel" 
              id="mobilePhone" 
              name="mobilePhone" 
              value={formData.mobilePhone || ''} 
              onChange={handleChange} 
              placeholder="(555) 123-4567"
              className={getError('mobilePhone') ? 'is-invalid' : ''}
            />
             {getError('mobilePhone') && <span className="error-text">{getError('mobilePhone')}</span>}
         </div>
         <div className="form-group">
            <label htmlFor="homePhone">Home Phone</label>
            <input 
              type="tel" 
              id="homePhone" 
              name="homePhone" 
              value={formData.homePhone || ''} 
              onChange={handleChange} 
              placeholder="(555) 987-6543"
              className={getError('homePhone') ? 'is-invalid' : ''}
            />
             {getError('homePhone') && <span className="error-text">{getError('homePhone')}</span>}
         </div>
      </div>

       <div className="form-group">
         <label htmlFor="address" className="required">Street Address</label>
         <input 
           type="text" 
           id="address" 
           name="address" 
           value={formData.address || ''} 
           onChange={handleChange} 
           required 
           className={getError('address') ? 'is-invalid' : ''}
         />
          {getError('address') && <span className="error-text">{getError('address')}</span>}
      </div>

      <div className="form-row">
         <div className="form-group">
            <label htmlFor="city" className="required">City</label>
            <input 
              type="text" 
              id="city" 
              name="city" 
              value={formData.city || ''} 
              onChange={handleChange} 
              required 
              className={getError('city') ? 'is-invalid' : ''}
            />
             {getError('city') && <span className="error-text">{getError('city')}</span>}
         </div>
         <div className="form-group">
            <label htmlFor="state" className="required">State</label>
            <input 
              type="text" 
              id="state" 
              name="state" 
              value={formData.state || ''} 
              onChange={handleChange} 
              required 
              maxLength={2} 
              placeholder="CA"
              className={getError('state') ? 'is-invalid' : ''}
            />
            {getError('state') && <span className="error-text">{getError('state')}</span>}
         </div>
          <div className="form-group">
            <label htmlFor="zipCode" className="required">Zip Code</label>
            <input 
              type="text" 
              id="zipCode" 
              name="zipCode" 
              value={formData.zipCode || ''} 
              onChange={handleChange} 
              required 
              pattern="\d{5}" 
              maxLength={5} 
              placeholder="90210"
              className={getError('zipCode') ? 'is-invalid' : ''}
            />
             {getError('zipCode') && <span className="error-text">{getError('zipCode')}</span>}
         </div>
      </div>

      <div className="form-group">
            <label htmlFor="preferredContact" className="required">Preferred Contact Method</label>
            <select
              id="preferredContact"
              name="preferredContact"
              value={formData.preferredContact || ''}
              onChange={handleChange}
              required
              className={getError('preferredContact') ? 'is-invalid' : ''}
            >
                <option value="" disabled>Select...</option>
                <option value="email">Email</option>
                <option value="mobile">Mobile Phone</option>
                <option value="home">Home Phone</option>
            </select>
            {getError('preferredContact') && <span className="error-text">{getError('preferredContact')}</span>}
      </div>
    </div>
  );
}

export default Step2Customer;